import * as React from 'react'

import type { LossReasonRow } from '@sdr-agend/admin-schemas'

/**
 * "Motivos de Perda" (Story 10.8, 2026-07-30, user request, AC4) — gráfico de
 * barras horizontal, SVG puro (mesma convenção dos outros componentes desta
 * página). Série única (contagem por motivo) — uma única cor semântica
 * (rose, mesmo tom de "Cancelados" em `AppointmentStatusDonut`, já que é uma
 * métrica de perda), sem paleta categórica (não há múltiplas séries
 * comparadas, só categorias de uma mesma métrica). `data` já vem ordenado
 * (`COUNT DESC`) por `queryLossReasonBreakdown` — o componente não reordena.
 */
export interface LossReasonChartProps {
  data: ReadonlyArray<LossReasonRow>
}

const BAR_COLOR_CLASS = 'bg-rose-500'
const BAR_COLOR_HEX = '#f43f5e'
const ROW_HEIGHT = 28
const LABEL_WIDTH = 140
const CHART_WIDTH = 480

export function LossReasonChart({ data }: LossReasonChartProps): React.ReactElement {
  if (data.length === 0) {
    return (
      <div
        className="flex h-24 items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground"
        data-testid="loss-reason-chart-empty"
      >
        Nenhum motivo de perda sincronizado no período selecionado.
      </div>
    )
  }

  const maxCount = Math.max(1, ...data.map((r) => r.count))
  const barAreaWidth = CHART_WIDTH - LABEL_WIDTH
  const height = data.length * ROW_HEIGHT

  return (
    <div data-testid="loss-reason-chart">
      <svg
        width="100%"
        viewBox={`0 0 ${CHART_WIDTH} ${height}`}
        role="img"
        aria-label={`Motivos de perda: ${data.map((r) => `${r.reason} ${r.count}`).join(', ')}`}
        preserveAspectRatio="xMidYMid meet"
      >
        {data.map((row, i) => {
          const y = i * ROW_HEIGHT
          const barWidth = (row.count / maxCount) * (barAreaWidth - 8)
          return (
            <g key={row.reason} data-testid={`loss-reason-row-${i}`}>
              <text
                x={LABEL_WIDTH - 8}
                y={y + ROW_HEIGHT / 2}
                textAnchor="end"
                dominantBaseline="middle"
                className="fill-muted-foreground text-[11px]"
              >
                {row.reason}
              </text>
              <rect
                x={LABEL_WIDTH}
                y={y + 5}
                width={barWidth}
                height={ROW_HEIGHT - 10}
                fill={BAR_COLOR_HEX}
                rx={2}
                className={BAR_COLOR_CLASS}
              />
              <text
                x={LABEL_WIDTH + barWidth + 6}
                y={y + ROW_HEIGHT / 2}
                dominantBaseline="middle"
                className="fill-foreground text-[11px] font-medium tabular-nums"
              >
                {row.count.toLocaleString('pt-BR')}
              </text>
            </g>
          )
        })}
      </svg>
    </div>
  )
}

export default LossReasonChart
